import { useState } from 'react';
import { FlaskConical, Plus, Loader2, Shield, ChevronDown, Pencil, Trash2, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/client';
import { SOURCE_LABELS } from '../utils/vitalConfig';

const FIELD = 'w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none';
const INLINE = 'border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20';

const STATUS_CFG = {
  normal:   { label: 'Normal',   color: 'bg-emerald-50 text-emerald-600 border border-emerald-100' },
  abnormal: { label: 'Abnormal', color: 'bg-amber-50 text-amber-600 border border-amber-100' },
  critical: { label: 'Critical', color: 'bg-red-50 text-red-600 border border-red-100' },
  pending:  { label: 'Pending',  color: 'bg-slate-100 text-slate-500' },
};

export default function LabResultsCard({
  labResults = [],
  canView = true,
  patientId = null,
  isDoctor = false,
  onAdded,
}) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    testName: '', loincCode: '', value: '', unit: '',
    referenceRange: '', status: 'pending', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleAdd = async () => {
    if (!form.testName.trim() || !form.value.trim()) {
      toast.error('Test name and value are required');
      return;
    }
    setSaving(true);
    try {
      const endpoint = isDoctor && patientId
        ? `/patient-profile/${patientId}/lab-results`
        : null;
      if (!endpoint) { toast.error('Only doctors can enter lab results'); setSaving(false); return; }
      await api.post(endpoint, form);
      toast.success('Lab result saved');
      setForm({ testName: '', loincCode: '', value: '', unit: '', referenceRange: '', status: 'pending', notes: '' });
      setShowForm(false);
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save lab result');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (lr) => {
    setEditingId(lr._id);
    setEditForm({
      testName: lr.testName,
      value: lr.value,
      unit: lr.unit || '',
      referenceRange: lr.referenceRange || '',
      status: lr.status || 'pending',
      notes: lr.notes || '',
    });
  };

  const handleEdit = async (lrId) => {
    if (!editForm.value?.trim()) { toast.error('Value is required'); return; }
    setSavingEdit(true);
    try {
      const endpoint = isDoctor && patientId
        ? `/patient-profile/${patientId}/lab-results/${lrId}`
        : null;
      if (!endpoint) { toast.error('Only doctors can edit lab results'); setSavingEdit(false); return; }
      await api.patch(endpoint, editForm);
      toast.success('Lab result updated');
      setEditingId(null);
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update lab result');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (lrId, source) => {
    setDeletingId(lrId);
    try {
      const endpoint = isDoctor && patientId
        ? `/patient-profile/${patientId}/lab-results/${lrId}`
        : `/patient-profile/me/lab-results/${lrId}`;
      await api.delete(endpoint);
      toast.success('Lab result removed');
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete lab result');
    } finally {
      setDeletingId(null);
    }
  };

  const displayed = expanded ? labResults : labResults.slice(0, 5);

  return (
    <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-headline text-base font-bold text-slate-900">Lab Results</h3>
            {canView && (
              <p className="text-xs text-slate-400 font-semibold">{labResults.length} results</p>
            )}
          </div>
        </div>
        {canView && isDoctor && (
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Result
          </button>
        )}
      </div>

      {!canView ? (
        <div className="flex items-center gap-2 py-6 justify-center">
          <Shield className="w-4 h-4 text-slate-400" />
          <p className="text-sm text-slate-400">Lab results access not granted</p>
        </div>
      ) : (
        <>
          {/* Add Form — doctor only */}
          {showForm && isDoctor && (
            <div className="mb-5 p-4 bg-slate-50 rounded-2xl border border-outline-variant/15 space-y-3">
              <p className="text-xs font-extrabold text-primary uppercase tracking-[0.15em]">Enter Lab Result</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Test Name *</label>
                  <input type="text" placeholder="e.g. Hemoglobin A1c" value={form.testName}
                    onChange={e => setForm(f => ({ ...f, testName: e.target.value }))} className={FIELD} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Value *</label>
                  <input type="text" placeholder="e.g. 6.2" value={form.value}
                    onChange={e => setForm(f => ({ ...f, value: e.target.value }))} className={FIELD} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Unit</label>
                  <input type="text" placeholder="e.g. %" value={form.unit}
                    onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} className={FIELD} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Reference Range</label>
                  <input type="text" placeholder="e.g. < 5.7%" value={form.referenceRange}
                    onChange={e => setForm(f => ({ ...f, referenceRange: e.target.value }))} className={FIELD} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Status</label>
                  <div className="relative">
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={`${FIELD} pr-9`}>
                      <option value="pending">Pending</option>
                      <option value="normal">Normal</option>
                      <option value="abnormal">Abnormal</option>
                      <option value="critical">Critical</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">LOINC Code</label>
                  <input type="text" placeholder="e.g. 4548-4" value={form.loincCode}
                    onChange={e => setForm(f => ({ ...f, loincCode: e.target.value }))} className={FIELD} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Notes (optional)</label>
                <input type="text" placeholder="Clinical notes" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={FIELD} />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button onClick={handleAdd} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save Result
                </button>
              </div>
            </div>
          )}

          {/* Results List */}
          {labResults.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No lab results on file</p>
          ) : (
            <div className="space-y-2.5">
              {displayed.map((lr, i) => {
                const st = STATUS_CFG[lr.status] || STATUS_CFG.pending;
                const src = SOURCE_LABELS[lr.source] || SOURCE_LABELS.self_reported;
                const isEditing = editingId === lr._id;
                const isDeleting = deletingId === lr._id;
                const canEdit = isDoctor;
                const canDelete = isDoctor || lr.source === 'self_reported';

                return (
                  <div key={lr._id || i} className="p-3.5 bg-slate-50 rounded-xl border border-outline-variant/10 hover:bg-white transition-colors">
                    {isEditing ? (
                      /* Inline edit form */
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input className={INLINE} value={editForm.testName}
                            onChange={e => setEditForm(f => ({ ...f, testName: e.target.value }))} placeholder="Test name" />
                          <input className={INLINE} value={editForm.value}
                            onChange={e => setEditForm(f => ({ ...f, value: e.target.value }))} placeholder="Value" />
                          <input className={INLINE} value={editForm.unit}
                            onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))} placeholder="Unit" />
                          <input className={INLINE} value={editForm.referenceRange}
                            onChange={e => setEditForm(f => ({ ...f, referenceRange: e.target.value }))} placeholder="Ref range" />
                          <div className="relative">
                            <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                              className={`${INLINE} w-full pr-8 appearance-none`}>
                              <option value="pending">Pending</option>
                              <option value="normal">Normal</option>
                              <option value="abnormal">Abnormal</option>
                              <option value="critical">Critical</option>
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                          </div>
                          <input className={INLINE} value={editForm.notes}
                            onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setEditingId(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEdit(lr._id)} disabled={savingEdit}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all">
                            {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Normal row */
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-slate-900">{lr.testName}</p>
                            {lr.loincCode && (
                              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                LOINC {lr.loincCode}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {new Date(lr.recordedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {lr.recordedBy?.name && <span className="ml-2">· {lr.recordedBy.name}</span>}
                          </p>
                          {lr.referenceRange && <p className="text-[11px] text-slate-400 mt-0.5">Ref: {lr.referenceRange}</p>}
                          {lr.notes && <p className="text-[11px] text-slate-400 mt-0.5 italic">{lr.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex flex-col items-end gap-1.5">
                            <p className="text-base font-extrabold text-slate-900 tabular-nums">
                              {lr.value} <span className="text-xs font-semibold text-slate-400">{lr.unit}</span>
                            </p>
                            <div className="flex items-center gap-1.5 flex-wrap justify-end">
                              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${src.color}`}>{src.label}</span>
                            </div>
                          </div>
                          {/* Edit / Delete */}
                          <div className="flex flex-col gap-1 ml-1">
                            {canEdit && (
                              <button onClick={() => startEdit(lr)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors" title="Edit">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {canDelete && (
                              <button onClick={() => handleDelete(lr._id, lr.source)} disabled={isDeleting}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40" title="Delete">
                                {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {labResults.length > 5 && (
                <button onClick={() => setExpanded(e => !e)}
                  className="w-full text-xs font-bold text-primary hover:underline py-1 transition-colors">
                  {expanded ? 'Show less' : `Show all ${labResults.length} results`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
